import React, { useState, useEffect } from "react";
import { AtSign, Lock, User, Eye, EyeOff, Check, X } from "lucide-react";
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// Terms and Conditions Modal Component
const TermsAndConditionsModal = ({ isOpen, onClose, onAccept }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Terms and Conditions</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto flex-grow">
          <h3 className="text-lg font-semibold mb-3">1. Introduction</h3>
          <p className="mb-4 text-gray-700">
            Welcome to our service. These Terms and Conditions govern your use of our website,
            products, and services. By accessing or using our services, you agree to be bound
            by these Terms. If you disagree with any part of the terms, you may not access the service.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">2. User Accounts</h3>
          <p className="mb-4 text-gray-700">
            When you create an account with us, you must provide information that is accurate,
            complete, and current at all times. Failure to do so constitutes a breach of the Terms,
            which may result in immediate termination of your account on our service.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">3. Privacy Policy</h3>
          <p className="mb-4 text-gray-700">
            Your privacy is important to us. It is our policy to respect your privacy regarding any
            information we may collect from you across our website. We will only collect and use
            information in ways that are useful to you and consistent with your privacy rights.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">4. Content</h3>
          <p className="mb-4 text-gray-700">
            Our service allows you to post, link, store, share and otherwise make available certain
            information, text, graphics, videos, or other material. You are responsible for the content
            you post and its legality, reliability, and appropriateness.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">5. Termination</h3>
          <p className="mb-4 text-gray-700">
            We may terminate or suspend your account immediately, without prior notice or liability,
            for any reason whatsoever, including without limitation if you breach the Terms.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">6. Limitation of Liability</h3>
          <p className="mb-4 text-gray-700">
            In no event shall we be liable for any indirect, incidental, special, consequential or
            punitive damages, including without limitation, loss of profits, data, use, goodwill, or
            other intangible losses, resulting from your access to or use of or inability to access
            or use the service.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">7. Governing Law</h3>
          <p className="mb-4 text-gray-700">
            These Terms shall be governed and construed in accordance with the laws applicable in your
            jurisdiction, without regard to its conflict of law provisions.
          </p>
          
          <h3 className="text-lg font-semibold mb-3">8. Changes to Terms</h3>
          <p className="mb-4 text-gray-700">
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time.
            What constitutes a material change will be determined at our sole discretion.
          </p>
        </div>
        
        <div className="p-4 border-t flex justify-end space-x-4">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            Decline
          </button>
          <button 
            onClick={() => {
              onAccept();
              onClose();
            }}
            className="px-4 py-2 text-white bg-orange-600 rounded-md hover:bg-orange-700"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

const SuccessAnimation = () => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-8 rounded-lg shadow-xl text-center animate-bounce">
        <div className="flex justify-center mb-4">
          <div className="bg-green-500 text-white rounded-full p-4">
            <Check size={48} />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-green-600 mb-2">
          Registration Successful!
        </h2>
        <p className="text-gray-600 mb-4">
          Your account has been created successfully.
        </p>
        <p className="text-sm text-gray-500">
          Redirecting to login page...
        </p>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    termsAccepted: false,
    phone: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isRegistrationSuccessful, setIsRegistrationSuccessful] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  // Effect to check if submit should be enabled
  useEffect(() => {
    setIsSubmitEnabled(formData.termsAccepted);
  }, [formData.termsAccepted]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
    
    setTouched(prev => ({
      ...prev,
      phone: true
    }));
    
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validate this field on blur
    validateField(name);
  };

  const validateField = (fieldName) => {
    let error = '';

    switch (fieldName) {
      case 'firstName':
        if (!formData.firstName.trim()) {
          error = "First name is required";
        } else if (formData.firstName.length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
        
      case 'lastName':
        if (!formData.lastName.trim()) {
          error = "Last name is required";
        } else if (formData.lastName.length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
        
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
          error = "Email is required";
        } else if (!emailRegex.test(formData.email)) {
          error = "Invalid email format";
        }
        break;
        
      case 'phone':
        if (!formData.phone.trim()) {
          error = "Phone number is required";
        } else if (formData.phone.length < 6) {
          error = "Phone number is too short";
        }
        break;
        
      case 'password':
        if (!formData.password.trim()) {
          error = "Password is required";
        } else if (formData.password.length < 8) {
          error = "Password must be at least 8 characters";
        } else {
          // Additional password strength checks
          const hasUpperCase = /[A-Z]/.test(formData.password);
          const hasLowerCase = /[a-z]/.test(formData.password);
          const hasNumbers = /[0-9]/.test(formData.password);
          const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

          if (!(hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar)) {
            error = "Password must include uppercase, lowercase, numbers, and special characters";
          }
        }
        break;
        
      case 'confirmPassword':
        if (!formData.confirmPassword.trim()) {
          error = "Please confirm your password";
        } else if (formData.password !== formData.confirmPassword) {
          error = "Passwords do not match";
        }
        break;
        
      case 'termsAccepted':
        if (!formData.termsAccepted) {
          error = "You must accept the terms and conditions";
        }
        break;
        
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return !error;
  };

  const validateForm = () => {
    // Validate all fields
    const fieldNames = ['firstName', 'lastName', 'email', 'phone', 'password', 'confirmPassword', 'termsAccepted'];
    
    // Mark all fields as touched
    const newTouched = {};
    fieldNames.forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);
    
    // Validate each field
    const isValid = fieldNames.every(field => validateField(field));
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.termsAccepted) {
      toast.error('You must accept the terms and conditions');
      return;
    }

    if (!validateForm()) {
      // Show toast for validation errors
      toast.error('Please correct the errors in the form');
      return;
    }

    try {
      const response = await axios.post('https://atabackend.quindl.com/auth/register', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phone,
        password: formData.password,
        termsAccepted: formData.termsAccepted
      });

      if (response.data) {
        setIsRegistrationSuccessful(true);
        toast.success('Registration successful!');
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      console.error("Error during registration:", error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        toast.error(error.response.data.message || 'Registration failed. Please try again.');
      } else if (error.request) {
        // The request was made but no response was received
        toast.error('No response from server. Please try again.');
      } else {
        // Something happened in setting up the request that triggered an Error
        toast.error('An error occurred. Please try again.');
      }
    }
  };

  const openTermsModal = (e) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const handleAcceptTerms = () => {
    setFormData(prev => ({
      ...prev,
      termsAccepted: true
    }));
    
    // Clear any errors related to terms
    setErrors(prev => ({
      ...prev,
      termsAccepted: ''
    }));
  };

  return (
    <>
      {isRegistrationSuccessful && <SuccessAnimation />}
      <TermsAndConditionsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
        onAccept={handleAcceptTerms}
      />
      
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="bg-white shadow-md rounded-lg w-full max-w-md p-8">
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
            Create Your Account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="flex space-x-4">
              <div className="w-1/2">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-3 py-2 border ${
                      touched.firstName && errors.firstName ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <User
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                </div>
                {touched.firstName && errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>
              <div className="w-1/2">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-3 py-2 border ${
                      touched.lastName && errors.lastName ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <User
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                </div>
                {touched.lastName && errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full pl-10 pr-3 py-2 border ${
                    touched.email && errors.email ? 'border-red-300' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`}
                />
                <AtSign
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
              </div>
              {touched.email && errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone Number with react-phone-input-2 */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <PhoneInput
                country={'au'} // Default to Australia
                value={formData.phone}
                onChange={handlePhoneChange}
                inputProps={{
                  name: 'phone',
                  id: 'phone',
                  required: true,
                  onBlur: () => {
                    setTouched(prev => ({ ...prev, phone: true }));
                    validateField('phone');
                  }
                }}
                containerClass="phone-input"
                containerStyle={{ width: '100%' }}
                inputStyle={{
                  width: '100%',
                  height: '42px',
                  fontSize: '16px',
                  paddingLeft: '48px',
                  border: touched.phone && errors.phone ? '1px solid #FCA5A5' : '1px solid #D1D5DB',
                  borderRadius: '0.375rem'
                }}
                buttonStyle={{
                  border: touched.phone && errors.phone ? '1px solid #FCA5A5' : '1px solid #D1D5DB',
                  borderRight: 'none',
                  borderRadius: '0.375rem 0 0 0.375rem',
                  backgroundColor: 'white'
                }}
              />
              {touched.phone && errors.phone && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Password Fields */}
            <div className="flex space-x-4">
              <div className="w-1/2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-10 py-2 border ${
                      touched.password && errors.password ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <Lock
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>
              <div className="w-1/2">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-10 py-2 border ${
                      touched.confirmPassword && errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <Lock
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
                {touched.confirmPassword && errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="termsAccepted"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleChange}
                onBlur={handleBlur}
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              />
              <label
                htmlFor="termsAccepted"
                className="ml-2 block text-sm text-gray-900"
              >
                I accept the{" "}
                <button 
                  onClick={openTermsModal}
                  className="text-orange-600 hover:text-orange-500 font-medium"
                >
                  Terms and Conditions
                </button>
              </label>
            </div>
            {touched.termsAccepted && errors.termsAccepted && (
              <p className="text-red-500 text-xs mt-1">{errors.termsAccepted}</p>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={!isSubmitEnabled}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${isSubmitEnabled 
                    ? 'bg-orange-600 hover:bg-orange-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500`}
              >
                Create Account
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500 ml-1"
              >
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      {/* Custom styles for phone input */}
      <style jsx>{`
        .phone-input .selected-flag:hover, 
        .phone-input .selected-flag:focus {
          background-color: rgba(249, 115, 22, 0.05) !important;
        }
        .phone-input .selected-flag.open {
          background-color: rgba(249, 115, 22, 0.1) !important;
        }
      `}</style>
    </>
  );
};

export default RegisterPage;